import { Component, OnInit } from '@angular/core';
import { RestaurantService } from '../../../core/services/restaurant.service';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { RestaurantReviewsCreateComponent } from './restaurant-reviews-create/restaurant-reviews-create.component';
import { AuthService } from '../../../core/services/auth.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { first } from 'rxjs/operators';
import { RestaurantSlug } from '../../../core/enums';
import { User, Review, Restaurant } from '../../../core/models';
import { ConfigService } from '../../../core/services/config.service';
import { MainService } from '../../../core/services/main.service';
import { CommonModule } from '@angular/common';
import { ReviewComponent } from './review/review.component';
import { FilterReviewsPipe } from "../../../core/pipes/filter-reviews.pipe";
import { FormsModule } from '@angular/forms';
import { Inject, PLATFORM_ID } from '@angular/core';
// import { NgxMasonryModule } from 'ngx-masonry';
import { NgxGridModule } from '@egjs/ngx-grid';
import { NgxGalleryModule, NgxGalleryOptions, NgxGalleryImage, NgxGalleryAnimation } from '@kolkov/ngx-gallery';

@Component({
    selector: 'app-restaurant-reviews',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        NgbTooltipModule,
        TranslocoPipe,
        ReviewComponent,
        FilterReviewsPipe,
        // NgxMasonryModule,
        NgxGridModule,
        NgxGalleryModule
    ],
    templateUrl: './restaurant-reviews.component.html',
    styleUrls: ['./restaurant-reviews.component.css'],
})
export class RestaurantReviewsComponent implements OnInit {
    mobileThreshold: number = 870;
    masonryModule: any;

    user: User;
    reviews: Review[];
    averageRating: number;
    numRatings: {
        5: number;
        4: number;
        3: number;
        2: number;
        1: number;
    };
    searchReviews;
    userWroteReview: boolean = false;

    numFullStars: number = 0
    numHalfStars: number = 0
    numEmptyStars: number = 0

    galleryOptions: NgxGalleryOptions[];
    galleryImages: NgxGalleryImage[];

    restaurant: Restaurant;

    constructor(
        private mainService: MainService,
        private restaurantService: RestaurantService,
        private route: ActivatedRoute,
        private modalService: NgbModal,
        public authService: AuthService,
        public translate: TranslocoService,
        private router: Router,
        public configService: ConfigService,
        @Inject(PLATFORM_ID) private platformId: Object
    ) {
        this.mainService.restaurantLoaded$
            .subscribe(restaurant => {
                if (restaurant != null) {
                    this.restaurant = restaurant
                    this.getReviews(restaurant)
                }
            })
    }

    async ngOnInit() {
        this.galleryOptions = [
            {
                image: false,
                thumbnailsRemainingCount: true,
                width: '100%',
                height: '7rem',
                thumbnailsColumns: 4,
                arrowPrevIcon: 'fa fa-chevron-left',
                arrowNextIcon: 'fa fa-chevron-right',
                imageAnimation: NgxGalleryAnimation.Slide,
                previewAnimation: false
            },
            {
                breakpoint: 500,
                width: "100%",
                thumbnailsColumns: 3
            }
        ];
        this.numRatings = {
            5: 0,
            4: 0,
            3: 0,
            2: 0,
            1: 0,
        };

        // if (isPlatformBrowser(this.platformId)) {
        //     const { NgxMasonryModule } = await import('ngx-masonry');
        //     this.masonryModule = NgxMasonryModule;
        // }
    }

    generateBarGradient(percent): string {
        percent *= 100;
        if (this.restaurant.slug === RestaurantSlug.Maharani || this.restaurant.slug === RestaurantSlug.Diolo || this.restaurant.slug === RestaurantSlug.MerRouge || this.restaurant.slug === RestaurantSlug.PtitPlateau || this.restaurant.slug === RestaurantSlug.FanfarePizza || this.restaurant.slug === RestaurantSlug.InochiExpress || this.restaurant.slug === RestaurantSlug.Barranco || this.restaurant.slug === RestaurantSlug.PetitSeoul || this.restaurant.slug === RestaurantSlug.Darbar) {
            return (
                'linear-gradient(90deg, rgba(100,100,100,1)' +
                percent +
                '%, rgba(230,230,230,1) ' +
                percent +
                '%)'
            );
        }
        else {
            return (
                'linear-gradient(90deg, rgba(196,196,196,1)' +
                percent +
                '%, rgba(237,237,237,1) ' +
                percent +
                '%)'
            );
        }

    }

    getReviews(restaurant: Restaurant) {
        this.restaurantService.getReviews(restaurant.slug)
            .pipe(first())
            .subscribe(reviews => {
                this.reviews = reviews;
                this.extractReviewData();
            })
    }

    extractReviewData() {
        let ratingSum = 0;
        for (let review of this.reviews) {
            ratingSum += review.rating;
            this.numRatings[review.rating]++;
        }
        let ratingAverage = ratingSum / this.reviews.length;
        this.averageRating = Math.round(ratingAverage * 10) / 10;
        this.numFullStars = Math.floor(this.averageRating)
        this.numHalfStars = this.averageRating % 1 != 0 ? 1 : 0
        this.numEmptyStars = 5 - this.numFullStars - this.numHalfStars

        if (this.user) {
            this.reviews.forEach(review => {
                if (review.user?.id === this.user.id) this.userWroteReview = true;
            })
        }
        let galleryImages: NgxGalleryImage[] = [];
        this.reviews.reverse().forEach(review => {
            review.photos.forEach(photo => {
                galleryImages.push({
                    small: photo.url,
                    medium: photo.url,
                    big: photo.url,
                    description: photo.description[this.translate.getActiveLang()]
                })
            })
        });
        this.galleryImages = galleryImages;
    }

    writeReviewClicked() {
        const slug = this.restaurant.slug
        if (!this.authService.loggedIn()) {
            localStorage.setItem('redirect_url', '/restaurants/' + slug + '/reviews');
            this.router.navigate([this.translate.getActiveLang() + '/login'])
        } else {
            const modalRef = this.modalService.open(RestaurantReviewsCreateComponent);
            modalRef.componentInstance.slug = slug;
            modalRef.componentInstance.restaurant = this.restaurant;

            modalRef.result.then(
                res => {
                    if (res) {
                        this.restaurantService.getReviews(this.restaurant.slug)
                            .pipe(first())
                            .subscribe(
                                reviews => {
                                    this.reviews = reviews
                                    this.extractReviewData()
                                }
                            )
                    }
                }
            )
        }
    }

    getIconColor() {
        return this.configService.getReviewIconColor(this.restaurant.slug)
    }

    get isMobileWidth() {
        return (typeof window !== "undefined") && window.innerWidth < this.mobileThreshold;
    }

    get columns(): number {
        return this.isMobileWidth ? 1 : 2;
    }
}
